import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Snackbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useExtensions } from '../app/ExtensionsContext';
import { useTranslation } from '../app/LanguageContext';
import { EXTENSION_CATALOG, findExtension } from './extensionRegistry';

export default function ExtensionsPage() {
  const { translate } = useTranslation();
  const { extensions, loading, loadError, reload, enable, disable } = useExtensions();
  const [busy, setBusy] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const resolveExtension = (catalogItem) => {
    const remote = extensions.find((e) => e.id === catalogItem.id);
    return {
      ...catalogItem,
      enabled: remote ? remote.enabled : false,
      requiredExtensions: remote ? remote.required_extensions || [] : catalogItem.requiredExtensions,
    };
  };

  const dependencyMissing = (item) => {
    return item.requiredExtensions.some((reqId) => {
      const dep = extensions.find((e) => e.id === reqId);
      return !dep || !dep.enabled;
    });
  };

  const handleToggle = async (item) => {
    setBusy(item.id);
    try {
      if (item.enabled) {
        await disable(item.id);
        setSnack({ open: true, message: translate('extensions.disabled', { name: translate(item.nameKey) }), severity: 'info' });
      } else {
        await enable(item.id);
        setSnack({ open: true, message: translate('extensions.enabled', { name: translate(item.nameKey) }), severity: 'success' });
      }
    } catch (err) {
      setSnack({ open: true, message: err.message || translate('common.error'), severity: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {translate('extensions.page_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('extensions.page_subtitle')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => reload()}>
              {translate('auth.session_retry')}
            </Button>
          }
        >
          {translate('extensions.load_error')}
        </Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {EXTENSION_CATALOG.map((catalogItem) => {
            const item = resolveExtension(catalogItem);
            const Icon = catalogItem.icon;
            const missing = dependencyMissing(item);

            return (
              <Card
                key={item.id}
                variant="outlined"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: item.enabled ? 'primary.main' : 'divider',
                  position: 'relative',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: item.enabled ? 'primary.main' : 'action.hover',
                        color: item.enabled ? 'primary.contrastText' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {translate(item.nameKey)}
                        </Typography>
                        {item.enabled && (
                          <Chip
                            size="small"
                            icon={<CheckCircleOutlineIcon />}
                            label={translate('extensions.active')}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    {translate(item.descriptionKey)}
                  </Typography>

                  {item.requiredExtensions.length > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        {translate('extensions.requires')}:
                      </Typography>
                      {item.requiredExtensions.map((reqId) => {
                        const dep = findExtension(reqId);
                        const depEnabled = extensions.some((e) => e.id === reqId && e.enabled);
                        return (
                          <Chip
                            key={reqId}
                            size="small"
                            label={dep ? translate(dep.nameKey) : reqId}
                            color={depEnabled ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        );
                      })}
                    </Box>
                  )}
                </CardContent>

                <Divider />

                <CardActions sx={{ px: 2, py: 1.5 }}>
                  {!item.enabled && missing && (
                    <Typography variant="caption" color="warning.main" sx={{ flexGrow: 1 }}>
                      {translate('extensions.dependency_missing')}
                    </Typography>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    variant={item.enabled ? 'outlined' : 'contained'}
                    color={item.enabled ? 'error' : 'primary'}
                    disabled={busy === item.id || (!item.enabled && missing)}
                    onClick={() => handleToggle(item)}
                    startIcon={busy === item.id ? <CircularProgress size={14} color="inherit" /> : null}
                  >
                    {item.enabled
                      ? translate('extensions.deactivate')
                      : translate('extensions.activate')}
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
